import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const React = require("react");
import { useEffect, useMemo, useState } from "react";
import { partition } from "../../../../utils/array";
import { hasValue } from "../../../../utils/asyncState";
import { nonDefaultSearchParams } from "../../../../utils/browserQueryParams";
import { Container } from "../../../Container";
import { debouncedPush, nextMonth, nextWeek, pushEventAction, tomorrow } from "../../EventUtils";
import { Pager } from "../../../Pager";
import { EventOverviewFilter } from "./EventOverviewFilter";
import { activityHeaderId, EventOverviewResultBlock } from "./EventOverviewResult";
import { initialDetailFilters } from "../../EventDetails/EventFilters/EventFiltersState";
const debounce = require("lodash.debounce");
import { features } from "../../../../features";
export const EventOverview = (props)=>{
    var _events_Result_find, _events_CurrentParams, _events_CurrentParams1;
    const config = features().events;
    if (config == 'off') return /*#__PURE__*/ _jsx(_Fragment, {});
    const push = pushEventAction();
    if (!hasValue(props.events)) return /*#__PURE__*/ _jsx(React.Fragment, {}, "event-overview-loading");
    const events = props.events.value;
    const { Jumbo, jumboProps, filterUpdaters: { updatePage, updateQuery }, filterSettings } = props;
    const { from } = filterSettings;
    const scrollTarget = from ? (_events_Result_find = events.Result.find((e)=>e.Start > from)) === null || _events_Result_find === void 0 ? void 0 : _events_Result_find.Id : undefined;
    const detailLinkSearch = nonDefaultSearchParams(filterSettings, initialDetailFilters);
    const resultBlockProps = {
        scrollTarget,
        detailLinkSearch
    };
    // Set up display for result count
    const results = events.TotalResults == 1 ? 'resultaat' : 'resultaten';
    let count = `${events.TotalResults} ${results}`;
    // If the predicate holds for `from`, the partition is created,
    // otherwise all events are passed to the next partition.
    const partitionBy = (events, predicate)=>partition(events, (e)=>predicate(e.Start));
    const [today, afterToday] = partitionBy(events.Result, (d)=>d < tomorrow());
    const [isTomorrow, afterTomorrow] = partitionBy(afterToday, (d)=>d < tomorrow(1));
    // Sometimes the start of next week is later than the start of next month, so we check both
    const [thisWeek, fromNextWeek] = partitionBy(afterTomorrow, (d)=>d < nextWeek() && d < nextMonth());
    const [inNextWeek, pastNextWeek] = partitionBy(fromNextWeek, (d)=>d < nextWeek(1) && d < nextMonth(1));
    const [thisMonth, pastThisMonth] = partitionBy(pastNextWeek, (d)=>d < nextMonth());
    const [inNextMonth, later] = partitionBy(pastThisMonth, (d)=>d < nextMonth(1));
    const afterNextMonth = nextMonth(1).toLocaleString('nl-NL', {
        month: 'long'
    });
    // Pager variables
    const currentPage = ((_events_CurrentParams = events.CurrentParams) === null || _events_CurrentParams === void 0 ? void 0 : _events_CurrentParams.page) ?? 1;
    const pageSize = ((_events_CurrentParams1 = events.CurrentParams) === null || _events_CurrentParams1 === void 0 ? void 0 : _events_CurrentParams1.pageSize) ?? config.overviewPage.pageSize;
    const totalResults = events.TotalResults;
    return /*#__PURE__*/ _jsxs("div", {
        className: "general-page",
        children: [
            /*#__PURE__*/ _jsx(Jumbo, {
                ...jumboProps
            }),
            /*#__PURE__*/ _jsx(Container, {
                size: "m",
                gutter: true,
                children: /*#__PURE__*/ _jsx("section", {
                    id: "activities",
                    className: "activities",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "activities__container",
                        children: [
                            /*#__PURE__*/ _jsx(QueryField, {
                                query: filterSettings.query,
                                updateQuery: updateQuery
                            }),
                            /*#__PURE__*/ _jsx(EventOverviewFilter, {
                                ...props
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "activities__content a-content",
                                id: "activity_results",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "a-filter__header",
                                        id: activityHeaderId,
                                        children: /*#__PURE__*/ _jsx("h3", {
                                            className: "a-filter__title title--ms",
                                            children: count
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(EventOverviewResultBlock, {
                                        label: "Vandaag",
                                        events: today,
                                        ...resultBlockProps
                                    }),
                                    /*#__PURE__*/ _jsx(EventOverviewResultBlock, {
                                        label: "Morgen",
                                        events: isTomorrow,
                                        ...resultBlockProps
                                    }),
                                    /*#__PURE__*/ _jsx(EventOverviewResultBlock, {
                                        label: "Deze week",
                                        events: thisWeek,
                                        ...resultBlockProps
                                    }),
                                    /*#__PURE__*/ _jsx(EventOverviewResultBlock, {
                                        label: "Volgende week",
                                        events: inNextWeek,
                                        ...resultBlockProps
                                    }),
                                    /*#__PURE__*/ _jsx(EventOverviewResultBlock, {
                                        label: "Deze maand",
                                        events: thisMonth,
                                        ...resultBlockProps
                                    }),
                                    /*#__PURE__*/ _jsx(EventOverviewResultBlock, {
                                        label: "Volgende maand",
                                        events: inNextMonth,
                                        ...resultBlockProps
                                    }),
                                    /*#__PURE__*/ _jsx(EventOverviewResultBlock, {
                                        label: `In ${afterNextMonth} en later`,
                                        events: later,
                                        ...resultBlockProps
                                    }),
                                    /*#__PURE__*/ _jsx(Pager, {
                                        currentPage,
                                        pageSize,
                                        totalResults,
                                        includeContainer: false,
                                        setPage: (page)=>{
                                            push('Navigeer naar pagina', page.toString());
                                            updatePage(page);
                                        }
                                    })
                                ]
                            })
                        ]
                    })
                })
            })
        ]
    }, "event-overview-loaded");
};
const QueryField = (param)=>{
    let { query, updateQuery } = param;
    const push = pushEventAction();
    const [localQuery, setLocalQuery] = useState(query);
    useEffect(()=>{
        setLocalQuery(query);
    }, [
        query
    ]);
    const debouncedUpdateQuery = useMemo(()=>debounce(updateQuery, 500), []);
    return /*#__PURE__*/ _jsxs("div", {
        className: "activities__queryfield",
        children: [
            /*#__PURE__*/ _jsx("label", {
                className: "a-query__title",
                htmlFor: "search",
                children: "Zoek een activiteit:"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "form__item--icon right",
                children: [
                    /*#__PURE__*/ _jsx("input", {
                        className: "form__input",
                        id: "search",
                        type: "text",
                        placeholder: "Zoek een activiteit...",
                        value: localQuery,
                        onChange: (e)=>{
                            const value = e.target.value;
                            setLocalQuery(value);
                            debouncedPush(push, 'filter-text', value);
                            debouncedUpdateQuery(value);
                        },
                        autoComplete: "on"
                    }),
                    /*#__PURE__*/ _jsx("svg", {
                        width: "18",
                        height: "18",
                        "aria-hidden": "true",
                        children: /*#__PURE__*/ _jsx("use", {
                            xlinkHref: "#icon--menu-search"
                        })
                    })
                ]
            })
        ]
    });
};
